import { useProjectStore } from '@/features/projects/stores/project-store'
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store'
import type { ProjectFormData } from '@/features/projects/utils/validation'
import { getProject, getProjectMediaIds, listExportFiles } from '@/infrastructure/storage'
import { loadRenderQueue } from '@/features/export/hooks/use-render-queue-persistence'
import type { ExportFileEntry } from '@/infrastructure/storage'
import { useTimelineStore } from '@/features/timeline/stores/timeline-store'

export interface AssistantProjectContextSnapshot {
  projectId: string
  mediaProjectId: string
  name: string
  width: number
  height: number
  fps: number
}

export interface AssistantProjectDiskSnapshot {
  projectId: string
  mediaProjectId: string
  name: string
  projectJsonPath: string
  mediaLinksPath: string
  renderQueuePath: string
  project: Record<string, unknown>
  mediaLinks: {
    total: number
    mediaIds: string[]
  }
  renderQueue: {
    jobCount: number
    isPaused: boolean
    updatedAt: number | null
    jobs: Record<string, unknown>[]
  }
  exportFiles: ExportFileEntry[]
}

export interface AssistantProjectStructureAnalysis {
  project: {
    projectId: string
    name: string
    description: string
    schemaVersion: number | null
    width: number | null
    height: number | null
    fps: number | null
    updatedAt: number | null
    thumbnailId: string | null
  }
  paths: {
    projectJsonPath: string
    mediaLinksPath: string
    renderQueuePath: string
  }
  timeline: {
    trackCount: number
    itemCount: number
    transitionCount: number
    compositionCount: number
    totalDurationFrames: number
    totalDurationSeconds: number
    currentFrame: number | null
    zoomLevel: number | null
    scrollPosition: number | null
    itemTypeBreakdown: Record<string, number>
    tracks: Array<{
      id: string
      name: string
      kind: string
      order: number | null
      itemCount: number
    }>
    items: Array<{
      id: string
      type: string
      trackId: string
      label: string
      fromFrame: number
      durationInFrames: number
      endFrame: number
      mediaId: string | null
      srcKind: 'blob' | 'file' | 'remote' | 'unknown' | null
    }>
  }
  mediaLinks: {
    total: number
    mediaIds: string[]
    linkedButUnusedMediaIds: string[]
    timelineReferencedMediaIds: string[]
    timelineUnlinkedMediaIds: string[]
  }
  exports: {
    total: number
    files: ExportFileEntry[]
  }
  renderQueue: {
    jobCount: number
    isPaused: boolean
    updatedAt: number | null
  }
  warnings: string[]
}

function toPlainJsonRecord(value: unknown): Record<string, unknown> {
  return JSON.parse(
    JSON.stringify(value, (key, nestedValue) => {
      if (key === 'rootFolderHandle') {
        return undefined
      }
      if (typeof nestedValue === 'function') {
        return undefined
      }
      return nestedValue
    }),
  ) as Record<string, unknown>
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function toNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toStringValue(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function detectSrcKind(src: unknown): 'blob' | 'file' | 'remote' | 'unknown' | null {
  if (typeof src !== 'string' || src.length === 0) return null
  if (src.startsWith('blob:')) return 'blob'
  if (src.startsWith('file:')) return 'file'
  if (src.startsWith('http://') || src.startsWith('https://')) return 'remote'
  return 'unknown'
}

export async function createAssistantProject(
  input: ProjectFormData,
): Promise<AssistantProjectContextSnapshot> {
  const project = await useProjectStore.getState().createProject(input)
  useMediaLibraryStore.getState().setCurrentProject(project.id)
  await useMediaLibraryStore.getState().loadMediaItems()

  return {
    projectId: project.id,
    mediaProjectId: project.id,
    name: project.name,
    width: project.metadata.width,
    height: project.metadata.height,
    fps: project.metadata.fps,
  }
}

export async function openAssistantProject(
  projectId: string,
): Promise<AssistantProjectContextSnapshot> {
  const project = await useProjectStore.getState().loadProject(projectId)
  if (!project) {
    throw new Error(`Project not found: ${projectId}`)
  }

  useMediaLibraryStore.getState().setCurrentProject(project.id)
  await useMediaLibraryStore.getState().loadMediaItems()

  return {
    projectId: project.id,
    mediaProjectId: project.id,
    name: project.name,
    width: project.metadata.width,
    height: project.metadata.height,
    fps: project.metadata.fps,
  }
}

export async function duplicateAssistantProject(
  projectId: string,
): Promise<AssistantProjectContextSnapshot> {
  // Persist the active in-memory timeline before duplicating so the draft
  // project inherits the user's latest edits instead of an older disk snapshot.
  await useTimelineStore.getState().saveTimeline(projectId, { skipThumbnailGeneration: true })

  const projectStore = useProjectStore.getState()
  await projectStore.loadProjects()
  const duplicatedProject = await projectStore.duplicateProject(projectId)
  return openAssistantProject(duplicatedProject.id)
}

export function getCurrentProject(): AssistantProjectContextSnapshot {
  const currentProject = useProjectStore.getState().currentProject
  const mediaProjectId = useMediaLibraryStore.getState().currentProjectId

  if (!currentProject) {
    throw new Error('No current project is open.')
  }

  const resolvedMediaProjectId = mediaProjectId ?? currentProject.id
  if (!resolvedMediaProjectId) {
    throw new Error('Project context is incomplete.')
  }

  return {
    projectId: currentProject.id,
    mediaProjectId: resolvedMediaProjectId,
    name: currentProject.name,
    width: currentProject.metadata.width,
    height: currentProject.metadata.height,
    fps: currentProject.metadata.fps,
  }
}

export async function readCurrentProjectDiskSnapshot(): Promise<AssistantProjectDiskSnapshot> {
  const currentProject = getCurrentProject()
  const persistedProject = await getProject(currentProject.projectId)
  if (!persistedProject) {
    throw new Error(`Project not found on disk: ${currentProject.projectId}`)
  }

  const [mediaIds, renderQueue, exportFiles] = await Promise.all([
    getProjectMediaIds(currentProject.projectId),
    loadRenderQueue(currentProject.projectId),
    listExportFiles(currentProject.projectId),
  ])

  return {
    projectId: currentProject.projectId,
    mediaProjectId: currentProject.mediaProjectId,
    name: persistedProject.name,
    projectJsonPath: `projects/${currentProject.projectId}/project.json`,
    mediaLinksPath: `projects/${currentProject.projectId}/media-links.json`,
    renderQueuePath: `projects/${currentProject.projectId}/render-queue.json`,
    project: toPlainJsonRecord(persistedProject),
    mediaLinks: {
      total: mediaIds.length,
      mediaIds,
    },
    renderQueue: {
      jobCount: renderQueue?.jobs.length ?? 0,
      isPaused: renderQueue?.isPaused ?? false,
      updatedAt: renderQueue?.updatedAt ?? null,
      jobs: (renderQueue?.jobs ?? []).map((job) => toPlainJsonRecord(job)),
    },
    exportFiles,
  }
}

export async function analyzeCurrentProjectStructure(): Promise<AssistantProjectStructureAnalysis> {
  const snapshot = await readCurrentProjectDiskSnapshot()
  const projectRecord = snapshot.project
  const timeline = asRecord(projectRecord.timeline)
  const metadata = asRecord(projectRecord.metadata)
  const trackRecords = Array.isArray(timeline?.tracks) ? timeline.tracks : []
  const itemRecords = Array.isArray(timeline?.items) ? timeline.items : []
  const transitionRecords = Array.isArray(timeline?.transitions) ? timeline.transitions : []
  const compositionRecords = Array.isArray(timeline?.compositions) ? timeline.compositions : []

  const normalizedItems = itemRecords
    .map((value) => asRecord(value))
    .filter((value): value is Record<string, unknown> => value !== null)
  const normalizedTracks = trackRecords
    .map((value) => asRecord(value))
    .filter((value): value is Record<string, unknown> => value !== null)

  const itemTypeBreakdown: Record<string, number> = {}
  const timelineReferencedMediaIds = new Set<string>()

  const items = normalizedItems.map((item) => {
    const type = toStringValue(item.type) ?? 'unknown'
    const mediaId = toStringValue(item.mediaId)
    const fromFrame = toNumber(item.from) ?? 0
    const durationInFrames = toNumber(item.durationInFrames) ?? 0
    const endFrame = fromFrame + durationInFrames
    itemTypeBreakdown[type] = (itemTypeBreakdown[type] ?? 0) + 1
    if (mediaId) {
      timelineReferencedMediaIds.add(mediaId)
    }

    return {
      id: toStringValue(item.id) ?? 'unknown-item',
      type,
      trackId: toStringValue(item.trackId) ?? 'unknown-track',
      label: toStringValue(item.label) ?? '',
      fromFrame,
      durationInFrames,
      endFrame,
      mediaId,
      srcKind: detectSrcKind(item.src),
    }
  })

  const tracks = normalizedTracks.map((track) => {
    const trackId = toStringValue(track.id) ?? 'unknown-track'
    return {
      id: trackId,
      name: toStringValue(track.name) ?? trackId,
      kind: toStringValue(track.kind) ?? 'unknown',
      order: toNumber(track.order),
      itemCount: items.filter((item) => item.trackId === trackId).length,
    }
  })

  const linkedMediaIds = new Set(snapshot.mediaLinks.mediaIds)
  const linkedButUnusedMediaIds = snapshot.mediaLinks.mediaIds.filter(
    (mediaId) => !timelineReferencedMediaIds.has(mediaId),
  )
  const timelineUnlinkedMediaIds = [...timelineReferencedMediaIds].filter(
    (mediaId) => !linkedMediaIds.has(mediaId),
  )

  const warnings: string[] = []
  if (items.some((item) => item.srcKind === 'blob')) {
    warnings.push('Timeline items contain blob: src URLs; prefer mediaId for stable analysis.')
  }
  if (timelineUnlinkedMediaIds.length > 0) {
    warnings.push(
      `Timeline references ${timelineUnlinkedMediaIds.length} media item(s) missing from media-links.json.`,
    )
  }
  if (linkedButUnusedMediaIds.length > 0) {
    warnings.push(
      `${linkedButUnusedMediaIds.length} linked media item(s) are not currently referenced on the timeline.`,
    )
  }

  const totalDurationFrames = items.reduce(
    (maxFrame, item) => Math.max(maxFrame, item.endFrame),
    0,
  )
  const fps = toNumber(metadata?.fps)

  return {
    project: {
      projectId: snapshot.projectId,
      name: toStringValue(projectRecord.name) ?? snapshot.name,
      description: toStringValue(projectRecord.description) ?? '',
      schemaVersion: toNumber(projectRecord.schemaVersion),
      width: toNumber(metadata?.width),
      height: toNumber(metadata?.height),
      fps,
      updatedAt: toNumber(projectRecord.updatedAt),
      thumbnailId: toStringValue(projectRecord.thumbnailId),
    },
    paths: {
      projectJsonPath: snapshot.projectJsonPath,
      mediaLinksPath: snapshot.mediaLinksPath,
      renderQueuePath: snapshot.renderQueuePath,
    },
    timeline: {
      trackCount: tracks.length,
      itemCount: items.length,
      transitionCount: transitionRecords.length,
      compositionCount: compositionRecords.length,
      totalDurationFrames,
      totalDurationSeconds: totalDurationFrames / Math.max(1, fps ?? 1),
      currentFrame: toNumber(timeline?.currentFrame),
      zoomLevel: toNumber(timeline?.zoomLevel),
      scrollPosition: toNumber(timeline?.scrollPosition),
      itemTypeBreakdown,
      tracks,
      items,
    },
    mediaLinks: {
      total: snapshot.mediaLinks.total,
      mediaIds: snapshot.mediaLinks.mediaIds,
      linkedButUnusedMediaIds,
      timelineReferencedMediaIds: [...timelineReferencedMediaIds],
      timelineUnlinkedMediaIds,
    },
    exports: {
      total: snapshot.exportFiles.length,
      files: snapshot.exportFiles,
    },
    renderQueue: {
      jobCount: snapshot.renderQueue.jobCount,
      isPaused: snapshot.renderQueue.isPaused,
      updatedAt: snapshot.renderQueue.updatedAt,
    },
    warnings,
  }
}
